import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle, Sparkles, Clock, Info, X, Film, Ear } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useEtudiantByUserId } from '@/features/etudiants/hooks/use-etudiants'
import { useUpdateTacheTimer } from '@/features/etudiants/hooks/use-agenda'
import { agendaApi } from '@/features/etudiants/api/agenda.api'
import { TopBar } from '@/design-system/layouts/top-bar'
import { soundHarpe } from '@/lib/sounds'

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function ScanMobilePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { data: etudiant } = useEtudiantByUserId(user?.id)
  const [searchParams] = useSearchParams()
  const updateTimer = useUpdateTacheTimer()

  const { t } = useTranslation()

  const isAuditive = etudiant?.typeMemoire === 'AUDITIVE'
  const ScanIcon = isAuditive ? Ear : Film
  const scanPhrase = isAuditive ? t('mobile.scan.phraseAuditive') : t('mobile.scan.phraseVisual')

  const dureeMinutes = parseInt(searchParams.get('duree') || '5', 10)
  const tacheAgendaId = searchParams.get('tacheAgendaId') ? parseInt(searchParams.get('tacheAgendaId')!, 10) : null
  const initialSeconds = searchParams.get('timerSeconds') ? parseInt(searchParams.get('timerSeconds')!, 10) : dureeMinutes * 60

  const [timeLeft, setTimeLeft] = useState(initialSeconds)
  const [isRunning, setIsRunning] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [breathPhase, setBreathPhase] = useState<'inhale' | 'exhale'>('inhale')
  const [showInfo, setShowInfo] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const totalSeconds = dureeMinutes * 60

  const saveTimer = useCallback(
    (seconds: number) => {
      if (tacheAgendaId) {
        updateTimer.mutate({ tacheId: tacheAgendaId, timerSeconds: seconds })
      }
    },
    [tacheAgendaId, updateTimer],
  )

  // Timer countdown
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false)
            setIsCompleted(true)
            saveTimer(0)
            soundHarpe()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isRunning, timeLeft, saveTimer])

  // Auto-complete scan via API when timer reaches 0
  useEffect(() => {
    if (isCompleted && tacheAgendaId) {
      agendaApi.terminerTache(tacheAgendaId)
        .then(() => queryClient.invalidateQueries({ queryKey: ['agenda'] }))
        .catch(() => { })
    }
  }, [isCompleted, tacheAgendaId])

  // Breathing cycle (4s inhale + 4s exhale)
  useEffect(() => {
    if (!isRunning) {
      setBreathPhase('inhale')
      return
    }
    const interval = setInterval(() => {
      setBreathPhase((p) => (p === 'inhale' ? 'exhale' : 'inhale'))
    }, 4000)
    return () => clearInterval(interval)
  }, [isRunning])

  const handleToggle = () => {
    if (isCompleted) return
    const wasRunning = isRunning
    setIsRunning(!isRunning)
    if (wasRunning) saveTimer(timeLeft)
  }

  // "Terminer" — mark scan complete + go to dashboard
  const handleTerminer = async () => {
    if (isRunning) {
      setIsRunning(false)
    }
    if (tacheAgendaId) {
      try {
        await agendaApi.terminerTache(tacheAgendaId)
        await queryClient.invalidateQueries({ queryKey: ['agenda'] })
      } catch {
        // Continue even on error
      }
    }
    navigate('/dashboard')
  }

  // "Plus tard" — go back without completing (tasks will be blocked)
  const handlePlusLater = async () => {
    if (isRunning) {
      setIsRunning(false)
    }
    if (tacheAgendaId) {
      try {
        await agendaApi.updateTacheTimer(tacheAgendaId, timeLeft)
        await queryClient.invalidateQueries({ queryKey: ['agenda'] })
      } catch {
        // Continue even on error
      }
    }
    navigate('/dashboard?fromScan=1')
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col"
      style={{ background: 'linear-gradient(180deg, #E8F4FD 0%, #EFF8F0 40%, #FDF8F0 100%)' }}
    >
      <TopBar />

      {/* Header */}
      <div className="px-6 pt-5 text-center">
        <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <ScanIcon className="h-7 w-7 text-neutral-800" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-800">
          {isCompleted ? t('mobile.scan.bravo') : t('mobile.scan.greeting', { name: user?.prenom || '' })}
        </h1>
        <p className="mt-1 text-base text-neutral-800">
          {isCompleted ? t('mobile.scan.sessionComplete') : scanPhrase}
        </p>
        {!isCompleted && (
          <button
            onClick={() => setShowInfo(true)}
            className="mt-3 flex items-center gap-1.5 mx-auto rounded-full bg-emerald-50 border border-neutral-800 px-3 py-1.5 text-xs font-semibold text-neutral-800 transition-colors active:opacity-70"
          >
            <Info className="h-3.5 w-3.5" />
            {t('mobile.scan.moreDetails')}
          </button>
        )}
      </div>

      {/* Info modal */}
      {showInfo && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center" onClick={() => setShowInfo(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg animate-[slideUp_0.3s_ease-out] rounded-t-3xl bg-white pb-[max(env(safe-area-inset-bottom,16px),16px)] pt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-200" />
            <button
              onClick={() => setShowInfo(false)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-800"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="px-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
                  <ScanIcon className="h-7 w-7 text-neutral-800" />
                </div>
                <h3 className="text-lg font-bold text-neutral-800">{t('mobile.scan.modalTitle')}</h3>
              </div>
              <div className="mt-4 space-y-1.5">
                <p className="text-sm leading-relaxed text-neutral-800">{t('mobile.scan.modalLine1')}</p>
                <p className="text-sm leading-relaxed text-neutral-800">{t('mobile.scan.modalLine2')}</p>
                <p className="text-sm leading-relaxed text-neutral-800">{t('mobile.scan.modalLine3')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breathing circle — tappable for play/pause */}
      <div className="flex flex-1 flex-col items-center justify-center gap-5">
        <div
          className="relative flex cursor-pointer items-center justify-center"
          onClick={handleToggle}
          style={{ width: 300, height: 300 }}
        >
          {/* Outer glow ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: isRunning ? (breathPhase === 'inhale' ? 295 : 235) : 265,
              height: isRunning ? (breathPhase === 'inhale' ? 295 : 235) : 265,
              background: isCompleted
                ? 'radial-gradient(circle, rgba(110,231,183,0.15) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(107,155,210,0.12) 0%, transparent 70%)',
              transition: 'width 4s ease-in-out, height 4s ease-in-out',
            }}
          />

          {/* Main breathing circle */}
          <div
            className="flex flex-col items-center justify-center rounded-full"
            style={{
              width: isRunning ? (breathPhase === 'inhale' ? 230 : 180) : 205,
              height: isRunning ? (breathPhase === 'inhale' ? 230 : 180) : 205,
              background: isCompleted
                ? 'linear-gradient(135deg, #6EE7B7, #34D399)'
                : 'linear-gradient(135deg, #93C5FD, #86EFAC)',
              boxShadow: isCompleted
                ? '0 20px 60px rgba(52,211,153,0.3)'
                : '0 20px 60px rgba(107,155,210,0.25)',
              transition: 'width 4s ease-in-out, height 4s ease-in-out, background 0.5s ease',
            }}
          >
            {isCompleted ? (
              <>
                <Sparkles className="h-14 w-14 text-white drop-shadow-sm" />
                <span className="mt-1 text-lg font-bold text-white">{t('mobile.scan.timerComplete')}</span>
              </>
            ) : (
              <>
                {/* Timer */}
                <span className="font-mono text-[3.2rem] font-extralight leading-none tracking-tight text-white drop-shadow-sm">
                  {formatTime(timeLeft)}
                </span>

                {/* Breathing guide */}
                {isRunning && (
                  <span className="mt-1.5 text-base font-medium text-white/80" key={breathPhase}>
                    {breathPhase === 'inhale' ? t('mobile.scan.inhale') : t('mobile.scan.exhale')}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Message */}
        <p className="max-w-[300px] text-center text-sm italic leading-relaxed text-neutral-400">
          {isCompleted
            ? t('mobile.scan.completedGuide')
            : isRunning
              ? t('mobile.scan.runningGuide')
              : t('mobile.scan.tapToStart')}
        </p>
      </div>

      {/* Bottom buttons */}
      <div className="flex flex-col items-center gap-3 pb-[max(calc(env(safe-area-inset-bottom,16px)+20px),36px)] pt-2">
        {isCompleted ? (
          <button
            onClick={handleTerminer}
            className="flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/25 transition-transform active:scale-95"
          >
            <Sparkles className="h-4 w-4" />
            {t('mobile.scan.continueDay')}
          </button>
        ) : (
          <>
            {/* Terminer — complete the scan */}
            <button
              onClick={handleTerminer}
              className="flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-500/25 transition-transform active:scale-95"
            >
              <CheckCircle className="h-4 w-4" />
              {t('mobile.scan.finish')}
            </button>

            {/* Plus tard — defer, tasks blocked */}
            <button
              onClick={handlePlusLater}
              className="flex items-center gap-1.5 text-sm font-medium text-neutral-400 transition-colors active:text-neutral-600"
            >
              <Clock className="h-3.5 w-3.5" />
              {t('mobile.scan.later')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
